import React, {useMemo, useState} from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary, Button, CircularProgress,
    Divider,
    Grid,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import {Season} from "../../schemas/seasons/seasonsSchema";
import SeasonAutocomplete from "../../components/input/SeasonAutocomplete";
import useSWR from "swr";
import {Race, RaceStatus} from "../../schemas/races/racesSchema";
import {deleteFetcher, getFetcher, postFetcher, putFetcher} from "../../utils/api/fetchers";
import {useAuth} from "../../utils/hooks/useAuth";
import {UserAccountDetails} from "../../schemas/user/userAccountDetailsSchemas";
import UserRole from "../../schemas/user/roles";
import {ExpandMore} from "@mui/icons-material";
import dayjs from "dayjs";
import ReportIcon from '@mui/icons-material/Report';
import {useNotification} from "../../utils/hooks/useNotification";
import {useNavigate} from "react-router-dom";
import {useConfirm} from "material-ui-confirm";
import {CategoryShort} from "../../schemas/category/categoriesSchema";

const RaceOrganize: React.FC = () => {
    const {user, hasPermission} = useAuth();
    const {showNotification} = useNotification();
    const confirm = useConfirm();
    const navigate = useNavigate();
    const [season, setSeason] = useState<Season | null>(null);
    const {data: userDetails, isLoading: isUserLoading} = useSWR<UserAccountDetails>(
        user ? `/users/${user.id}/details` : null, getFetcher
    );
    const {data: races, isLoading: isRacesLoading, mutate} = useSWR<Race[]>(
        season ? `/seasons/${season.id}/races` : null, getFetcher
    );
    const isLoading = isUserLoading || isRacesLoading;

    // filter races in season that user can organize (has their id in organizedRaceIds), if user is admin, add all
    const assignedRaces = useMemo(() =>
            (hasPermission(UserRole.ADMIN) ? races :
                races?.filter(race => userDetails?.organizedRaceIds.includes(race.id))) || []
        , [races, userDetails, hasPermission]);

    const groupedRaces = useMemo(() => {
        const sortByDate = (races: Race[]) => races
            .slice()
            .sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));

        return {
            PLANNED: sortByDate(assignedRaces.filter(r => r.status === 'PLANNED')),
            IN_PROGRESS: sortByDate(assignedRaces.filter(r => r.status === 'IN_PROGRESS')),
            FINISHED: sortByDate(assignedRaces.filter(r => r.status === 'FINISHED'))
        }
    }, [assignedRaces]);

    const getStatusLabel = (status: RaceStatus) => {
        switch (status) {
            case 'PLANNED':
                return 'Nadcházející závody';
            case 'IN_PROGRESS':
                return 'Probíhající závody';
            case 'FINISHED':
                return 'Ukončené závody';
            default:
                return status;
        }
    };

    const handleContinueOrganizing = (raceId: number) => {
        navigate(`/races/organize/${raceId}`);
        showNotification("Pokračování v organizaci závodu.", "info");
    }

    const handleOrganize = (raceId: number) => {
        confirm({
            description: `Opravdu chcete spustit tento závod?`,
            title: "Začít závod",
            confirmationText: "Potvrdit",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "primary"},
        })
            .then(() => {
                putFetcher(`/races/${raceId}/status/start`, {})
                    .then(() => {
                        navigate(`/races/organize/${raceId}`);
                        showNotification("Závod byl úspěšně zahájen.", "success");
                    })
                    .catch((error) => {
                        showNotification("Chyba při organizaci závodu.", "error");
                    });
            })
            .catch(() => {
            });
    }

    const publishResults = async (raceId: number) => {
        try {
            await putFetcher(`/races/${raceId}/status/results`, {
                resultStatus: "PUBLISHED"
            });
            showNotification("Výsledky závodu byly úspěšně publikovány.", "success");
            mutate();

            // Try check if season results exist, if yes, delete them, if no, generate them
            const categories = await getFetcher<CategoryShort[]>("/categories");
            if (!categories || !season) {
                showNotification("Nepodařilo se načíst kategorie či sezónu.", "error");
                return false;
            }
            const endpoints = [
                `/seasons/${season.id}/results/overall`,
                ...categories.map(cat => `/seasons/${season.id}/results/categories/${cat.id}`)
            ];
            const results = await Promise.allSettled(
                endpoints.map(url =>
                    getFetcher(url)
                        .then(() => true)
                        .catch(() => false)
                )
            );
            const anySuccess = results.some(r => r.status === 'fulfilled' && r.value === true);
            if (anySuccess) {
                try {
                    await deleteFetcher(`/seasons/${season.id}/results`);
                } catch (e) {
                    showNotification("Nepodařilo se smazat sezónní výsledky", "error");
                    console.error(e);
                    return false;
                }
            }

            // Generate season results
            try {
                await postFetcher(`/seasons/${season?.id}/results/generate`, {});
                showNotification("Sezónní výsledky byly úspěšně přepočítány.", "success");
            } catch (error: any) {
                if (error.response?.status === 404) {
                    showNotification("Sezóna neobsahuje žádné výsledky závodů pro vypočítání.", "error");
                } else if (error.response?.status === 409) {
                    showNotification("Sezónní výsledky již existují.", "error");
                } else {
                    showNotification("Chyba při přepočítávání sezónních výsledků.", "error");
                }
            }

        } catch (error: any) {
            if (error.response?.status === 404) {
                showNotification("Závod nebyl nalezen.", "error");
            } else {
                showNotification("Chyba při publikaci výsledků závodu.", "error");
            }
        }
    }

    const handlePublishResults = (raceId: number) => {
        confirm({
            description: <Stack>
                <Typography>Opravdu chcete publikovat veřejně výsledky tohoto závodu?</Typography>
                <Typography>Výsledky budou dostupné pro všechny uživatele a dojde k přepočítání sezónních
                    výsledků.</Typography>
            </Stack>,
            title: "Zveřejnění výsledků závodu",
            confirmationText: "Zveřejnit",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "primary"},
        })
            .then(() => publishResults(raceId))
            .catch(() => {
            });
    }

    const handleChangeToInProgress = (raceId: number) => {
        confirm({
            description: `Opravdu chcete znovu spustit tento závod?`,
            title: "Obnovit průběh závodu",
            confirmationText: "Potvrdit",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "error"},
        })
            .then(() => {
                putFetcher(`/races/${raceId}/status`, {status: "IN_PROGRESS"})
                    .then(() => {
                        showNotification("Závod byl úspěšně změněn na probíhající.", "success");
                        mutate();
                    })
                    .catch((error) => {
                        showNotification("Chyba při změně závodu na probíhající.", "error");
                    });
            })
            .catch(() => {
            });
    }

    return (
        <Grid container spacing={2}>
            {/* Left Panel */}
            <Grid item xs={12} sm={2}>
                <Stack direction="column" spacing={2}>
                    <Typography variant="h5">
                        Závody k organizaci
                    </Typography>
                    <SeasonAutocomplete onSeasonChange={setSeason} selectedSeason={season}/>
                </Stack>
            </Grid>

            {/* Center Panel */}
            <Grid item xs={12} sm={8}>
                {isLoading ? (
                    <Stack alignItems="center" justifyContent="center" height="100%">
                        <CircularProgress/>
                    </Stack>
                ) : (
                    <Stack direction="column" spacing={1} alignItems="center">
                        {(['IN_PROGRESS', 'PLANNED', 'FINISHED'] as RaceStatus[]).map(status => (
                            <Accordion key={status} defaultExpanded={status === 'PLANNED' || status === 'IN_PROGRESS'}
                                       sx={{width: '100%', maxWidth: "100%"}}>
                                <AccordionSummary expandIcon={<ExpandMore/>}>
                                    <Typography fontWeight="bold">{getStatusLabel(status)}</Typography>
                                </AccordionSummary>
                                <Divider/>
                                <AccordionDetails>
                                    <Stack spacing={1} sx={{px: 1}}>
                                        {groupedRaces[status].length > 0 ? (
                                            groupedRaces[status].map(race => (
                                                <Stack key={race.id}
                                                       direction={{xs: "column", sm: "row"}}
                                                       justifyContent="space-between"
                                                       sx={{py: 1.5, px: 1, borderBottom: '1px solid #ddd'}}>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        {race.status === 'FINISHED' && race.resultStatus === "UNPUBLISHED" && (
                                                            <Tooltip
                                                                title="Závod byl ukončen, ale výsledky nejsou zveřejněny."
                                                                placement="top">
                                                                <ReportIcon color="warning" fontSize="small"/>
                                                            </Tooltip>
                                                        )}
                                                        <Typography variant="body2">
                                                            <strong>{race.name}</strong> – {dayjs(race.date).format('DD.MM.YYYY')}
                                                        </Typography>
                                                    </Stack>

                                                    <Stack direction={{xs: "column", sm: "row"}} spacing={1}
                                                           alignItems="flex-end" sx={{mt: {xs: 1, sm: 0}}}>
                                                        {race.status === 'IN_PROGRESS' && (
                                                            <Button variant="contained" color="success"
                                                                    onClick={() => handleContinueOrganizing(race.id)}>
                                                                Pokračovat v organizaci
                                                            </Button>
                                                        )}
                                                        {race.status === 'PLANNED' && (
                                                            <Button variant="contained" color="primary"
                                                                    onClick={() => handleOrganize(race.id)}>
                                                                Organizovat
                                                            </Button>
                                                        )}
                                                        {race.status === 'FINISHED' && (
                                                            <Stack direction={{xs: "column", sm: "row"}} spacing={1}
                                                                   alignItems="center">
                                                                {race.resultStatus === "UNPUBLISHED" && (
                                                                    <Button variant="outlined" color="primary"
                                                                            onClick={() => handlePublishResults(race.id)}>
                                                                        Publikovat výsledky
                                                                    </Button>
                                                                )}
                                                                {hasPermission(UserRole.COMMODORE) && (
                                                                    <Button variant="outlined" color="warning"
                                                                            onClick={() => handleChangeToInProgress(race.id)}>
                                                                        Změnit na probíhající
                                                                    </Button>
                                                                )}
                                                            </Stack>
                                                        )}
                                                    </Stack>
                                                </Stack>
                                            ))
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Žádné závody k zobrazení.
                                            </Typography>
                                        )}
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Stack>
                )}
            </Grid>
        </Grid>
    )
        ;
}

export default RaceOrganize;
